import { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { useIntl } from 'react-intl'
import SideModalWrapper from '../../../Assets/modals/SideModalWrapper'
import { messages } from '../../messages'
import {
  RouteDestination,
  LoadMetadata,
  DestinationActionTypeId,
  EditedLoadActionConfig,
} from '../plannerTypes'

type Service = {
  id: number
  name: string
  enabled: boolean
  customerSelectable: boolean
  loadTypes?: { loadTypeId: number; loadType?: { id: number; name: string } }[]
}

type EditActionsServicesSidePanelProps = {
  open: boolean
  onClose: () => void
  onSave: (updatedActions: EditedLoadActionConfig[], removedLoadIds: number[]) => void
  destination: RouteDestination
  loadMetadataById: Record<number, LoadMetadata>
  services: Service[]
}

const EditActionsServicesSidePanel = ({
  open,
  onClose,
  onSave,
  destination,
  loadMetadataById,
  services,
}: EditActionsServicesSidePanelProps) => {
  const { formatMessage } = useIntl()
  const [editedActions, setEditedActions] = useState<Record<number, EditedLoadActionConfig>>({})
  const [removedLoadIds, setRemovedLoadIds] = useState<number[]>([])

  // Initialize edited actions from destination's current load actions and load services
  useEffect(() => {
    if (open && destination) {
      const initialActions: Record<number, EditedLoadActionConfig> = {}
      destination.loadActions.forEach((action) => {
        // Get serviceIds for this load from loadServices array
        const serviceIdsForLoad = destination.loadServices
          .filter((s) => s.loadId === action.loadId)
          .map((s) => s.serviceId)
        initialActions[action.loadId] = {
          loadId: action.loadId,
          destinationActionTypeId: action.destinationActionTypeId,
          clearanceRequired: action.clearanceRequired || false,
          serviceIds: serviceIdsForLoad,
        }
      })
      setEditedActions(initialActions)
      setRemovedLoadIds([])
    }
  }, [open, destination])

  const loadIds = useMemo(() => {
      return destination.loadActions
        .map((action) => action.loadId)
        .filter((loadId) => !removedLoadIds.includes(loadId))
    }, [destination.loadActions, removedLoadIds])

  const handleActionTypeChange = (loadId: number, actionTypeId: number) => {
    setEditedActions((prev) => ({
      ...prev,
      [loadId]: {
        ...prev[loadId],
        destinationActionTypeId: actionTypeId,
      },
    }))
  }

  const handleClearanceChange = (loadId: number, clearanceRequired: boolean) => {
    setEditedActions((prev) => ({
      ...prev,
      [loadId]: {
        ...prev[loadId],
        clearanceRequired,
      },
    }))
  }

  const handleAddService = (loadId: number, serviceId: number) => {
    setEditedActions((prev) => {
      const current = prev[loadId]
      if (!current || current.serviceIds.includes(serviceId)) return prev

      return {
        ...prev,
        [loadId]: {
          ...current,
          serviceIds: [...current.serviceIds, serviceId],
        },
      }
    })
  }

  const handleRemoveService = (loadId: number, serviceId: number) => {
    setEditedActions((prev) => {
      const current = prev[loadId]
      if (!current) return prev

      return {
        ...prev,
        [loadId]: {
          ...current,
          serviceIds: current.serviceIds.filter((id) => id !== serviceId),
        },
      }
    })
  }

  const handleRemoveLoad = (loadId: number) => {
    setRemovedLoadIds((prev) => [...prev, loadId])
  }

  const handleSaveClick = () => {
    const actionsToSave = loadIds.map((loadId) => editedActions[loadId])
    onSave(actionsToSave, removedLoadIds)
    handleClose()
  }

  const handleClose = () => {
    setEditedActions({})
    setRemovedLoadIds([])
    onClose()
  }

  const getServiceName = (serviceId: number): string => {
    const service = services.find((s: { id: number; name: string }) => s.id === serviceId)
    return service?.name || `Service ${serviceId}`
  }

  const getAvailableServicesForLoad = (loadId: number) => {
    const metadata = loadMetadataById[loadId]
    const loadTypeId = metadata?.loadTypeId
    const config = editedActions[loadId]

    return services.filter((service: Service) => {
      // Must be enabled and customer selectable
      if (!service.enabled || !service.customerSelectable) return false
      // Must not already be selected
      if (config?.serviceIds?.includes(service.id)) return false
      // Must support the load type (if load types are defined)
      if (service.loadTypes && service.loadTypes.length > 0 && loadTypeId) {
        return service.loadTypes.some((lt) => lt.loadTypeId === loadTypeId)
      }
      return true
    })
  }

  const hasChanges = useMemo(() => {
    if (removedLoadIds.length > 0) return true

    for (const action of destination.loadActions) {
      const edited = editedActions[action.loadId]
      if (!edited) continue

      if (edited.destinationActionTypeId !== action.destinationActionTypeId) return true
      if (edited.clearanceRequired !== (action.clearanceRequired || false)) return true

      // Get original serviceIds from loadServices array
      const originalServiceIds = destination.loadServices
        .filter((s) => s.loadId === action.loadId)
        .map((s) => s.serviceId)
      if (edited.serviceIds.length !== originalServiceIds.length) return true
      if (!edited.serviceIds.every((id) => originalServiceIds.includes(id))) return true
    }

    return false
  }, [destination.loadActions, destination.loadServices, editedActions, removedLoadIds])

  return (
    <SideModalWrapper
      open={open}
      onClose={handleClose}
      headerLabel={formatMessage(messages.editActionsServices)}
      withBottomBox={false}
      width="900px"
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {formatMessage(messages.editActionsServicesDescription)}
        </Typography>

        {loadIds.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {formatMessage(messages.noLoadActionsAtDestination)}
          </Typography>
        ) : (
          <Table size="small" sx={{ mb: 3, '& .MuiTableCell-root': { py: 1, px: 1 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                  {formatMessage(messages.load)}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                  {formatMessage(messages.pickUpDropOff)}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                  {formatMessage(messages.clearance)}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                  {formatMessage(messages.service)}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                  {formatMessage(messages.selectedServices)}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem', width: 60 }}>
                  {formatMessage(messages.actions)}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadIds.map((loadId) => {
                const metadata = loadMetadataById[loadId]
                const config = editedActions[loadId]
                return (
                  <TableRow key={loadId}>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {metadata?.trackingNumber || '-'}
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={config?.destinationActionTypeId || DestinationActionTypeId.Pickup}
                          onChange={(e) => handleActionTypeChange(loadId, e.target.value as number)}
                        >
                          <MenuItem value={DestinationActionTypeId.Pickup}>
                            {formatMessage(messages.pickup)}
                          </MenuItem>
                          <MenuItem value={DestinationActionTypeId.DropOff}>
                            {formatMessage(messages.dropoff)}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleClearanceChange(loadId, !config?.clearanceRequired)}
                        sx={{ color: config?.clearanceRequired ? 'success.main' : 'error.main' }}
                        aria-label={config?.clearanceRequired ? 'Clearance required' : 'Clearance not required'}
                      >
                        {config?.clearanceRequired ? <CheckIcon /> : <CloseIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                          value=""
                          displayEmpty
                          onChange={(e) => {
                            const serviceId = Number(e.target.value)
                            if (serviceId) {
                              handleAddService(loadId, serviceId)
                            }
                          }}
                        >
                          <MenuItem value="" disabled>
                            {formatMessage(messages.addService)}
                          </MenuItem>
                          {getAvailableServicesForLoad(loadId).map(
                            (service: { id: number; name: string }) => (
                              <MenuItem key={service.id} value={service.id}>
                                {service.name}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {config?.serviceIds && config.serviceIds.length > 0 ? (
                          config.serviceIds.map((serviceId) => (
                            <Chip
                              key={serviceId}
                              label={getServiceName(serviceId)}
                              size="small"
                              onDelete={() => handleRemoveService(loadId, serviceId)}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {formatMessage(messages.noServicesSelected)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveLoad(loadId)}
                        aria-label={formatMessage(messages.removeLoadFromDestination)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button variant="outlined" onClick={handleClose}>
            {formatMessage(messages.cancel)}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveClick}
            disabled={!hasChanges}
          >
            {formatMessage(messages.save)}
          </Button>
        </Box>
      </Box>
    </SideModalWrapper>
  )
}

export default EditActionsServicesSidePanel
